import React, { useEffect, useState } from 'react';
import IPage from '../../interfaces/IPage';
import { Container } from './InternalUserList.styled';
import PageTitle from '../../components/page-title/PageTitle';
import {
  Backdrop,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { BiAddToQueue } from 'react-icons/bi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInternalUserService } from '../../hooks/services/useInternalUserService';
import { useInternalUserAccessScopeService } from '../../hooks/services/useInternalUserAccessScopeService';
import IInternalUser from '../../interfaces/IInternalUser';
import IInternalUserAccessScope from '../../interfaces/IInternalUserAccessScope';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import CustomTableSortLabel from '../../components/custom-table-sort-label/CustomTableSortLabel';
import { AiTwotoneEdit } from 'react-icons/ai';
import moment from 'moment';
import { BsTrash } from 'react-icons/bs';
import CustomTablePagination from '../../components/custom-table-pagination/CustomTablePagination';
import { MdOutlineAssignmentReturn } from 'react-icons/md';
import InternalUserAccessScopeForm from './InternalUserAccessScopeForm';

const InternalUserAccessScopeList = (props: IPage) => {
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('createdAt');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<IInternalUser>();
  const [scopes, setScopes] = useState<IInternalUserAccessScope[]>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [action, setAction] = useState<'new' | 'edit'>('edit');
  const [id, setId] = useState<string>('');

  window.document.title = props.title;

  const navigate = useNavigate();
  const location = useLocation();
  const userService = useInternalUserService();
  const userAccessScopeService = useInternalUserAccessScopeService();
  const toastr = useToastrVanila();

  useEffect(() => {
    const userId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    listUser(userId);
    listScopes(perPage, currentPage, orderField, orderDirection);
  }, []);

  const listUser = async (userIdParam: string) => {
    setLoading(true);
    await userService
      .listById(userIdParam)
      .then(result => {
        setUser(result);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateScopeList = () => {
    listScopes(perPage, currentPage, orderField, orderDirection);
  };

  const listScopes = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
  ) => {
    setLoading(true);
    const userId = location.pathname.split('/')[location.pathname.split('/').length - 1];
    await userAccessScopeService
      .listByUserId(
        userId,
        `perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${orderDirection}`,
      )
      .then(result => {
        setTotalRows(result.totalRows);
        setScopes(result.data);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const remove = async (id: string) => {
    setLoading(true);
    await userAccessScopeService
      .remove(id)
      .then(async result => {
        toastr.success('Abrangência excluída com sucesso!');
        await listScopes(perPage, currentPage, orderField, orderDirection);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  return (
    <Container>
      <PageTitle title={`LISTAGEM DE ABRANGÊNCIAS DO USUÁRIO ${user?.fullName?.toUpperCase()}`}>
        <div>
          <Tooltip title="Voltar para listagem de usuários" placement="left-start">
            <IconButton onClick={() => navigate('/user')}>
              <MdOutlineAssignmentReturn />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cadastrar novo" placement="left-start">
            <IconButton
              onClick={() => {
                setId(``);
                setAction('new');
                setIsOpen(true);
              }}
              color="inherit"
            >
              <BiAddToQueue />
            </IconButton>
          </Tooltip>
        </div>
      </PageTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="companyCode"
                label="Código da Empresa"
                handleRequest={listScopes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="subsidiaryCode"
                label="Código da Filial"
                handleRequest={listScopes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="createdAt"
                label="Data da Criação"
                handleRequest={listScopes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scopes &&
              scopes.map(scope => {
                return (
                  <TableRow key={scope.id} hover={true}>
                    <TableCell>{scope.companyCode}</TableCell>
                    <TableCell>{scope.subsidiaryCode}</TableCell>
                    <TableCell>{moment(`${scope.createdAt}`).format('DD/MM/yyyy HH:mm')}</TableCell>

                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton
                          onClick={() => {
                            setId(`${scope.id}`);
                            setAction('edit');
                            setIsOpen(true);
                          }}
                        >
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" placement="top">
                        <IconButton onClick={() => remove(`${scope.id}`)}>
                          <BsTrash style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        totalRows={totalRows}
        currentPage={currentPage}
        perPage={perPage}
        handleRequest={listScopes}
        setPerPage={setPerPage}
        setCurrentPage={setCurrentPage}
        orderField={orderField}
        orderDirection={orderDirection}
      />
      {isOpen && (
        <InternalUserAccessScopeForm
          isOpen={isOpen}
          id={id}
          action={action}
          setIsOpen={setIsOpen}
          updateScopeList={updateScopeList}
        />
      )}
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
export default InternalUserAccessScopeList;
